import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type DateRange as DayPickerRange,
  type DayProps,
  useDayRender,
} from 'react-day-picker';
import { format } from 'date-fns';
import { Calendar } from './ui/calendar';

/**
 * Range-selection calendar that overlays colored marker dots under each day
 * to show what data was logged on that date. Pure UI primitive — no presets,
 * no popover, no internal hook coupling. Two consumers share this:
 *
 *   1. `DateRangePicker`  — page-level, with preset chips + popover trigger
 *   2. `GenerateReportDialog` — inline in the report-generation dialog
 *
 * Marker constants (`MARKER_COLOR`, `MARKER_LABEL`, `MarkerKind`) are
 * exported so callers can build matching legends or stat blocks without
 * re-defining the color palette.
 */

export type MarkerKind = 'meal' | 'exercise' | 'emotion' | 'med';

/**
 * Canonical activity palette — shared by every surface that color-codes
 * activity types (calendar dots, Chronological Record chips,
 * DayActivityTimeline badges, popover icons). Picked for:
 *
 *   • Hue separation — purple (270°), red (0°), cyan (190°), amber
 *     (40°) sit far enough apart on the wheel to read distinct even
 *     at 6px dot size.
 *   • Domain fit — purple/lilac aligns with the patient report's meal
 *     accent; red is the medical convention for medications; cyan
 *     conveys movement without colliding with the macro-donut blue;
 *     amber reads as warmth for mood.
 *
 * Keep tone classes (`text-<color>-600`, `bg-<color>-100`) in
 * PatientRightPanel.tsx and DayActivityTimeline.tsx in sync with
 * these hex values when changing the palette.
 */
export const MARKER_COLOR: Record<MarkerKind, string> = {
  meal: '#8B5CF6', // violet-500
  med: '#C2410C', // orange-700 — burnt orange, reads as "medical/dose"
  exercise: '#06B6D4', // cyan-500
  emotion: '#F59E0B', // amber-500
};

export const MARKER_LABEL: Record<MarkerKind, string> = {
  meal: 'Meals',
  exercise: 'Exercise',
  emotion: 'Emotions',
  med: 'Medication',
};

const MARKER_KEYS: MarkerKind[] = ['meal', 'exercise', 'emotion', 'med'];

/**
 * Custom react-day-picker `Day` component that renders the default button
 * plus an absolutely-positioned strip of marker dots beneath the date number.
 * Routes through `useDayRender` so all built-in modifier classes (selected,
 * range start/end, today, outside) and click handlers stay intact — we just
 * append dots when the day has any of our custom marker modifiers active.
 *
 * Lives in module scope so React doesn't remount it on each parent render —
 * passing a component reference (vs an inline function) keeps the day grid
 * stable across selections.
 */
const DayWithMarkers = (props: DayProps) => {
  const ref = useRef<HTMLButtonElement>(null);
  const render = useDayRender(props.date, props.displayMonth, ref);

  if (render.isHidden) return <></>;
  if (!render.isButton) {
    // react-day-picker uses a non-button div for some range-middle states.
    return <div {...render.divProps} />;
  }

  const active = render.activeModifiers as Record<string, true | undefined>;
  const dots = MARKER_KEYS.filter((k) => active[k]);

  return (
    <button {...render.buttonProps} ref={ref}>
      <span className="relative inline-flex h-full w-full items-center justify-center">
        {format(props.date, 'd')}
        {dots.length > 0 && (
          <span
            // Absolutely positioned strip of dots below the date number.
            // `pointer-events-none` so clicks pass through to the day button.
            className="pointer-events-none absolute bottom-0.5 left-1/2 flex -translate-x-1/2 gap-0.5"
            aria-hidden
          >
            {dots.map((k) => (
              <span
                key={k}
                className="h-1 w-1 rounded-full"
                style={{ background: MARKER_COLOR[k] }}
              />
            ))}
          </span>
        )}
      </span>
    </button>
  );
};

type Props = {
  /** Currently-selected range. Undefined when nothing is picked. */
  value: { from: Date; to: Date } | undefined;
  /** Called with the new range once the user picks both ends. Intermediate
   *  one-ended selections fire with `to === from` (matches v1 UX). */
  onChange: (next: { from: Date; to: Date }) => void;
  /** Per-marker arrays of dates to decorate. Empty / undefined arrays are
   *  ignored; the legend hides marker kinds with no data. */
  markers?: Partial<Record<MarkerKind, Date[]>>;
  /** Default month shown when the calendar first opens. Defaults to the
   *  selected `from` if provided, else today. */
  defaultMonth?: Date;
  /** Number of months visible side-by-side. Default 2 (matches v1 + the
   *  existing DateRangePicker). Set to 1 to fit narrower containers. */
  numberOfMonths?: number;
  /** Earliest selectable day. Days before this are disabled. */
  fromDate?: Date;
  /** Latest selectable day. Days after this are disabled. */
  toDate?: Date;
  /** Hide the legend strip at the bottom (e.g. when the caller renders its
   *  own legend elsewhere on the page). */
  hideLegend?: boolean;
  /** Extra className applied to the wrapping div. */
  className?: string;
};

const startOfMonth = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), 1);
const addMonths = (d: Date, n: number): Date =>
  new Date(d.getFullYear(), d.getMonth() + n, 1);

export const MarkedRangeCalendar = ({
  value,
  onChange,
  markers,
  defaultMonth,
  numberOfMonths = 2,
  fromDate,
  toDate,
  hideLegend = false,
  className,
}: Props) => {
  const { t } = useTranslation();

  // The "displayed month" is the LEFT month of the visible window. With
  // numberOfMonths=2 the rightmost cell is `displayedMonth + 1`. Anchor it
  // so the range's END date lands in the rightmost month — that's what the
  // user expects when they just clicked "Last meal" or any quick-select
  // entry: the date they snapped to should be visible without having to
  // pan the calendar manually.
  const anchorMonthFor = (range: { from: Date; to: Date } | undefined): Date => {
    if (!range) return startOfMonth(defaultMonth ?? new Date());
    return startOfMonth(addMonths(range.to, -(Math.max(0, numberOfMonths - 1))));
  };

  // Controlled `month` so the calendar pans when the parent updates `value`
  // externally (e.g. via Quick Select). `defaultMonth` on react-day-picker
  // is only honored on first mount; setting it after won't pan the view.
  const [displayedMonth, setDisplayedMonth] = useState<Date>(() => anchorMonthFor(value));

  // Re-anchor whenever the value's END month moves outside the currently
  // visible window. Skips re-anchoring when the user is just navigating
  // within the same selection (e.g. paginating to look at an earlier month)
  // so we don't fight their manual pan.
  const lastAnchoredToRef = useRef<string>('');
  useEffect(() => {
    if (!value) return;
    // Cache key for the value's end date — only re-anchor when it actually
    // changed since the last anchor. This lets the user pan manually
    // without us snapping back on every render.
    const valueKey = `${value.to.getFullYear()}-${value.to.getMonth()}-${value.to.getDate()}`;
    if (lastAnchoredToRef.current === valueKey) return;
    lastAnchoredToRef.current = valueKey;

    // Visible window is [displayedMonth, displayedMonth + numberOfMonths).
    const windowEnd = addMonths(displayedMonth, numberOfMonths);
    const valueEnd = startOfMonth(value.to);
    const inWindow = valueEnd >= displayedMonth && valueEnd < windowEnd;
    if (!inWindow) {
      setDisplayedMonth(anchorMonthFor(value));
    }
    // anchorMonthFor / displayedMonth aren't in deps — including them
    // would cause re-runs that fight the user's manual pan.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.to.getTime(), numberOfMonths]);

  // Build a `modifiers` map keyed by marker name. `useDayRender` inside
  // `DayWithMarkers` exposes these as boolean flags on `activeModifiers`
  // so the day component can decide which dots to render.
  const modifiers = useMemo(() => {
    const m: Partial<Record<MarkerKind, Date[]>> = {};
    if (markers) {
      for (const key of MARKER_KEYS) {
        const dates = markers[key];
        if (dates && dates.length > 0) m[key] = dates;
      }
    }
    return m;
  }, [markers]);

  const onSelect = (next: DayPickerRange | undefined) => {
    if (!next?.from) return;
    // react-day-picker fires onChange with only `from` set on the first
    // click; emit a same-day range so callers can render an immediate
    // single-day selection. They'll get the proper {from, to} on click two.
    const to = next.to ?? next.from;
    onChange({ from: next.from, to });
  };

  return (
    <div className={className}>
      <Calendar
        mode="range"
        numberOfMonths={numberOfMonths}
        month={displayedMonth}
        onMonthChange={setDisplayedMonth}
        selected={value ? { from: value.from, to: value.to } : undefined}
        onSelect={onSelect}
        modifiers={modifiers}
        fromDate={fromDate}
        toDate={toDate}
        components={{ Day: DayWithMarkers }}
      />
      {!hideLegend && Object.keys(modifiers).length > 0 && (
        <div className="flex flex-wrap gap-3 border-t px-3 py-2 text-xs">
          {(Object.keys(modifiers) as MarkerKind[]).map((key) => (
            <div key={key} className="flex items-center gap-1.5">
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ background: MARKER_COLOR[key] }}
              />
              <span className="text-muted-foreground">{t(MARKER_LABEL[key])}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
